'use strict';

(function initializeBastionEnhancements(root) {
    const app = root.dndNextLevel = root.dndNextLevel || {};
    const documentRef = root.document;
    const openers = {
        takeBastionTurn: 'bastion-turn-modal',
        stickyTakeBastionTurn: 'bastion-turn-modal',
        openPendingBastionEventModal: 'pending-bastion-event-modal',
        'reset-local-bastion': 'reset-bastion-confirm-modal',
    };
    const closers = {
        closeBastionTurnModal: 'bastion-turn-modal',
        cancelBastionTurn: 'bastion-turn-modal',
        closePendingBastionEventModal: 'pending-bastion-event-modal',
        cancelPendingBastionEventModal: 'pending-bastion-event-modal',
        closeResetBastionConfirmModal: 'reset-bastion-confirm-modal',
        cancelResetBastion: 'reset-bastion-confirm-modal',
        closeBarrackModal: 'barrack-modal',
        cancelBarrackModal: 'barrack-modal',
        closeWarRoomModal: 'war-room-modal',
        cancelWarRoomModal: 'war-room-modal',
        closeWarRoomSoldiersModal: 'war-room-soldiers-modal',
        cancelWarRoomSoldiersModal: 'war-room-soldiers-modal',
        monsterReferenceCloseButton: 'war-room-reference-modal',
        'modal-alert-close': 'modal-alert',
    };

    const modal = id => documentRef.getElementById(id);
    const modalTriggers = new Map();
    const modalFocusTraps = new Map();
    const setWorkbenchView = (view, focusResult = false) => {
        const layout = documentRef.getElementById('bastion-workbench');
        if (!layout) return;
        layout.querySelectorAll('[data-workbench-view]').forEach(panel => {
            panel.classList.toggle('hidden', panel.dataset.workbenchView !== view);
        });
        layout.querySelectorAll('[data-workbench-view-target]').forEach(button => {
            button.setAttribute('aria-pressed', String(button.dataset.workbenchViewTarget === view));
        });
        if (focusResult && view === 'result') {
            documentRef.getElementById('bastion-result')?.focus({preventScroll: true});
        }
    };
    const open = (id, trigger) => {
        const element = modal(id);
        if (!element) return;
        if (trigger) modalTriggers.set(id, trigger);
        const focusTrap = app.dialogFocus?.create?.(element);
        if (focusTrap) modalFocusTraps.set(id, focusTrap);
        element.classList.remove('hidden');
        element.classList.add('flex');
        element.setAttribute('aria-hidden', 'false');
        element.querySelector('button, input, select, textarea')?.focus();
    };
    const close = id => {
        const element = modal(id);
        if (!element || element.classList.contains('hidden')) return;
        element.classList.add('hidden');
        element.classList.remove('flex');
        element.setAttribute('aria-hidden', 'true');
        modalTriggers.get(id)?.focus();
        modalTriggers.delete(id);
        modalFocusTraps.delete(id);
    };
    const trackerDocument = () => {
        const raw = documentRef.getElementById('bastiontracker')?.dataset?.persistedFragmentDocument;
        try {
            return raw ? JSON.parse(raw) : null;
        } catch (_error) {
            return null;
        }
    };
    const syncClientState = () => {
        app.bastion = app.bastion || {};
        app.bastion.bastion = trackerDocument();
    };
    const syncTurnButtons = () => {
        const tracker = documentRef.getElementById('bastiontracker');
        const hasTracker = Boolean(tracker);
        const pending = Boolean(documentRef.querySelector('#bastion-pending-event-panel:not(.hidden)'));
        const blocked = tracker?.dataset?.turnBlocked === 'true';
        documentRef.getElementById('bastion-primary-actions')?.classList.toggle('hidden', !hasTracker);
        ['takeBastionTurn', 'stickyTakeBastionTurn'].forEach(id => {
            const button = documentRef.getElementById(id);
            if (!button) return;
            button.disabled = !hasTracker || pending || blocked;
            button.setAttribute('aria-disabled', String(button.disabled));
        });
        const save = documentRef.getElementById('bastion-save-action');
        if (save) {
            save.disabled = !hasTracker;
            save.setAttribute('aria-disabled', String(save.disabled));
        }
    };
    const syncTradeControls = select => {
        const storehouse = select.id === 'pending-bastion-event-storehouse-trade-choice';
        const prefix = storehouse ? 'storehouse' : 'stable';
        const buying = select.value === 'BUY';
        documentRef.getElementById(`pending-bastion-event-${prefix}-buy-controls`)?.classList.toggle('hidden', !buying);
        documentRef.getElementById(`pending-bastion-event-${prefix}-sell-controls`)?.classList.toggle('hidden', buying);
    };
    const syncFacilityLevelOptions = () => {
        const level = Number(documentRef.getElementById('player-level-input')?.value || 0);
        const specialFacility = documentRef.getElementById('special-facility-input');
        documentRef.querySelectorAll('#special-facility-input option[data-level-requirement]').forEach(option => {
            option.disabled = level < Number(option.dataset.levelRequirement || 0);
        });
        if (specialFacility?.selectedOptions?.[0]?.disabled) specialFacility.value = '';
        const facilityLimit = level >= 17 ? 6 : level >= 13 ? 5 : level >= 9 ? 4 : 2;
        const facilityCount = trackerDocument()?.specialFacilities?.length || 0;
        const limitReached = facilityCount >= facilityLimit;
        documentRef.getElementById('special-facility-max-for-level-reached')?.classList.toggle('hidden', !limitReached);
        documentRef.getElementById('special-facility-selection')?.classList.toggle('hidden', limitReached);
    };
    const syncBasicFacilitySpace = () => {
        const basicFacility = documentRef.getElementById('basic-facility-input');
        const space = documentRef.getElementById('basic-facility-size-input');
        if (!space) return;
        const enabled = Boolean(basicFacility?.value);
        space.disabled = !enabled;
        if (!enabled) space.value = '';
    };
    const syncStableStartingMounts = () => {
        const stableSelected = documentRef.getElementById('special-facility-input')?.value === 'Stable';
        documentRef.getElementById('stable-starting-mounts-section')?.classList.toggle('hidden', !stableSelected);
        const largeMount = documentRef.getElementById('stable-large-mount-input');
        const smallMount = documentRef.getElementById('stable-small-mount-input');
        if (largeMount) {
            largeMount.disabled = !stableSelected;
            if (!stableSelected) largeMount.value = 'Riding Horse';
        }
        if (smallMount) {
            smallMount.disabled = !stableSelected;
            if (!stableSelected) smallMount.value = 'Pony';
        }
    };
    const syncSticky = () => {
        const dock = documentRef.getElementById('bastion-sticky-actions');
        const actions = documentRef.getElementById('bastion-primary-actions');
        const tracker = documentRef.getElementById('bastiontracker');
        const workbench = documentRef.getElementById('bastion-workbench');
        if (!dock || !actions || !tracker || !workbench) return;
        const workbenchRect = workbench.getBoundingClientRect();
        const shouldShow = root.innerWidth >= 1024
            && actions.getBoundingClientRect().bottom < 0
            && workbenchRect.top < root.innerHeight
            && workbenchRect.bottom > 0
            && !documentRef.querySelector('#bastion-turn-modal.flex');
        dock.classList.toggle('hidden', !shouldShow);
        dock.classList.remove('absolute', 'fixed');
        if (!shouldShow) {
            delete dock.dataset.positionMode;
            return;
        }
        const latched = workbenchRect.bottom <= root.innerHeight;
        dock.classList.add(latched ? 'absolute' : 'fixed');
        dock.dataset.positionMode = latched ? 'latched' : 'fixed';
    };
    const closeCompletedEditor = form => {
        if (form?.id === 'barrack-editor-form') close('barrack-modal');
        if (form?.id === 'war-room-editor-form') close('war-room-modal');
        if (form?.id === 'war-room-army-editor-form') close('war-room-soldiers-modal');
    };
    const syncArmyAssignments = () => {
        const count = documentRef.getElementById('war-room-army-lieutenant-count');
        const rows = Array.from(documentRef.querySelectorAll('[data-field="war-room-army-assignment-row"]'));
        if (!count || rows.length === 0) return;
        if (count.options.length !== rows.length) {
            count.replaceChildren(...rows.map((_row, index) => {
                const option = documentRef.createElement('option');
                option.value = String(index + 1);
                option.textContent = String(index + 1);
                return option;
            }));
            count.value = String(rows.length);
        }
        const visibleCount = Number(count.value || rows.length);
        rows.forEach((row, index) => {
            const active = index < visibleCount;
            row.classList.toggle('hidden', !active);
            row.querySelectorAll('input, select').forEach(input => { input.disabled = !active; });
        });
        const selected = rows.slice(0, visibleCount)
            .map(row => row.querySelector('[data-field="war-room-army-lieutenant-select"]')?.value)
            .filter(Boolean);
        rows.slice(0, visibleCount).forEach(row => {
            const select = row.querySelector('[data-field="war-room-army-lieutenant-select"]');
            if (!select) return;
            Array.from(select.options).forEach(option => {
                option.disabled = Boolean(option.value && option.value !== select.value && selected.includes(option.value));
            });
        });
    };
    const historyKey = bastion => {
        const character = (bastion?.characterName || 'unknown-character').trim().toLowerCase();
        const name = (bastion?.bastionName || 'unknown-bastion').trim().toLowerCase();
        return `dnd-next-level:bastion-turn-history:${character}:${name}`;
    };
    const readHistory = bastion => {
        try {
            const parsed = JSON.parse(root.localStorage?.getItem(historyKey(bastion)) || '[]');
            return Array.isArray(parsed) ? parsed : [];
        } catch (_error) {
            return [];
        }
    };
    const setHistoryExpanded = expanded => {
        const toggle = documentRef.getElementById('bastion-turn-history-toggle');
        const content = documentRef.getElementById('bastion-turn-history-content');
        const icon = documentRef.getElementById('bastion-turn-history-toggle-icon');
        toggle?.setAttribute('aria-expanded', String(expanded));
        content?.classList.toggle('hidden', !expanded);
        if (icon) icon.textContent = expanded ? '-' : '+';
    };
    const renderHistory = () => {
        const bastion = trackerDocument();
        const panel = documentRef.getElementById('bastion-turn-history-panel');
        const body = documentRef.getElementById('bastion-turn-history-body');
        const empty = documentRef.getElementById('bastion-turn-history-empty');
        if (!bastion || !panel || !body || !empty) return;
        const history = readHistory(bastion);
        body.replaceChildren();
        history.forEach(entry => {
            const row = documentRef.createElement('tr');
            [new Date(entry.recordedAt).toLocaleString(), entry.summary, entry.eventName || 'None', Array.isArray(entry.details) ? entry.details.join(' | ') : '']
                .forEach(value => {
                    const cell = documentRef.createElement('td');
                    cell.className = 'px-3 py-3 text-slate-700';
                    cell.textContent = value;
                    row.appendChild(cell);
                });
            body.appendChild(row);
        });
        panel.classList.toggle('hidden', history.length === 0);
        empty.classList.toggle('hidden', history.length > 0);
        setHistoryExpanded(false);
    };
    const recordTurn = () => {
        const bastion = trackerDocument();
        const summary = documentRef.getElementById('bastion-turn-summary')?.textContent?.trim();
        if (!bastion || !summary) return;
        const details = Array.from(documentRef.querySelectorAll('#bastion-turn-details-list li'))
            .map(item => item.textContent.trim()).filter(Boolean);
        const eventName = documentRef.getElementById('bastion-turn-event-name')?.textContent?.trim() || '';
        const history = [{recordedAt: new Date().toISOString(), summary, eventName, details}, ...readHistory(bastion)].slice(0, 10);
        try { root.localStorage?.setItem(historyKey(bastion), JSON.stringify(history)); } catch (_error) { /* optional */ }
        renderHistory();
    };
    let turnRequestPending = false;

    documentRef.addEventListener('click', event => {
        const button = event.target.closest?.('button');
        if (!button) return;
        if (button.dataset.workbenchViewTarget) {
            setWorkbenchView(button.dataset.workbenchViewTarget);
            return;
        }
        if (button.id === 'bastion-new-action') {
            setWorkbenchView('customize');
            documentRef.getElementById('bastionmenu-form')?.reset();
            const builder = documentRef.getElementById('bastion-options');
            if (builder) builder.scrollIntoView({behavior: 'smooth', block: 'start'});
            documentRef.getElementById('bastion-name-input')?.focus({preventScroll: true});
            return;
        }
        if (['takeBastionTurn', 'stickyTakeBastionTurn'].includes(button.id)
            && documentRef.getElementById('bastiontracker')?.dataset?.warRoomPlanRequired === 'true') {
            event.preventDefault();
            documentRef.querySelector('[data-field="war-room-configure-army-button"]')?.click();
            open('war-room-soldiers-modal');
            return;
        }
        if (openers[button.id]) open(openers[button.id], button);
        if (closers[button.id]) close(closers[button.id]);
        if (button.dataset.openBastionModal) open(button.dataset.openBastionModal, button);
        if (button.matches('[data-open-monster-reference]')) {
            const title = documentRef.getElementById('war-room-reference-title');
            const input = documentRef.getElementById('war-room-reference-name');
            if (title) title.textContent = button.dataset.monsterName || '';
            if (input) input.value = button.dataset.monsterName || '';
            documentRef.getElementById('war-room-reference-loader')?.click();
            open('war-room-reference-modal');
        }
        if (button.id === 'bastion-save-action') {
            const bastion = trackerDocument();
            if (bastion) app.bastionRecentAdapter?.saveCurrent?.(bastion, root.localStorage, true);
        }
        if (button.id === 'bastion-turn-history-toggle') {
            setHistoryExpanded(button.getAttribute('aria-expanded') !== 'true');
        }
    });

    documentRef.addEventListener('change', event => {
        if (['pending-bastion-event-storehouse-trade-choice', 'pending-bastion-event-stable-trade-choice'].includes(event.target.id)) {
            syncTradeControls(event.target);
        }
        if (event.target.id === 'player-level-input') syncFacilityLevelOptions();
        if (event.target.id === 'player-level-input' || event.target.id === 'special-facility-input') syncStableStartingMounts();
        if (event.target.id === 'basic-facility-input') syncBasicFacilitySpace();
        if (event.target.id === 'war-room-army-lieutenant-count'
            || event.target.matches?.('[data-field="war-room-army-lieutenant-select"]')) {
            syncArmyAssignments();
        }
    });
    documentRef.addEventListener('input', event => {
        if (event.target.id !== 'pending-bastion-event-storehouse-trade-value') return;
        const display = documentRef.getElementById('pending-bastion-event-storehouse-trade-value-display');
        if (display) display.textContent = `${event.target.value} GP`;
    });
    documentRef.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
            Object.values({...openers, ...closers}).forEach(close);
            return;
        }
        if (event.key === 'Tab') {
            const activeModal = documentRef.querySelector('[role="dialog"].flex');
            if (activeModal) modalFocusTraps.get(activeModal.id)?.handleKeydown(event);
        }
    });
    documentRef.addEventListener('htmx:beforeRequest', event => {
        const action = event.detail?.elt?.action || '';
        if (event.detail?.elt?.id === 'bastionmenu-form') {
            const status = documentRef.getElementById('bastion-builder-status');
            if (status) status.textContent = 'Updating Bastion tracker...';
        }
        if (action.endsWith('/bastion/turns')) turnRequestPending = true;
        if (action.endsWith('/bastion/reset')) {
            try { root.localStorage?.removeItem('dnd-next-level:bastion-current'); } catch (_error) { /* optional */ }
        }
    });
    documentRef.addEventListener('htmx:afterRequest', event => {
        if (event.detail?.elt?.id === 'bastionmenu-form') {
            const status = documentRef.getElementById('bastion-builder-status');
            const retargeted = event.detail?.xhr?.getResponseHeader?.('HX-Retarget') === '#bastion-options';
            if (status) {
                status.textContent = event.detail?.successful
                    ? (retargeted ? 'Check the highlighted Bastion options.' : 'Bastion tracker updated.')
                    : 'Bastion tracker could not be updated.';
            }
            if (event.detail?.successful) setWorkbenchView(retargeted ? 'customize' : 'result', !retargeted);
        }
        if (event.detail?.successful && event.detail?.elt?.matches?.('[data-persisted-fragment-restore]')) {
            setWorkbenchView('result', true);
        }
        if (!event.detail?.successful) return;
        closeCompletedEditor(event.detail.elt);
        if (event.detail.elt?.action?.endsWith('/bastion/turns')) close('bastion-turn-modal');
        if (event.detail.elt?.id === 'pending-bastion-event-resolution-form') close('pending-bastion-event-modal');
        if (event.detail.elt?.action?.endsWith('/bastion/reset')) {
            try { root.localStorage?.removeItem('dnd-next-level:bastion-current'); } catch (_error) { /* optional */ }
        }
    });
    documentRef.addEventListener('htmx:afterSettle', () => {
        syncClientState();
        syncTurnButtons();
        syncFacilityLevelOptions();
        syncBasicFacilitySpace();
        syncStableStartingMounts();
        syncSticky();
        const pending = documentRef.querySelector('#bastion-pending-event-panel:not(.hidden)');
        if (turnRequestPending && !pending) {
            recordTurn();
            app.main?.showModalAlert?.('Bastion turn resolved.');
        }
        turnRequestPending = false;
        renderHistory();
        syncArmyAssignments();
        if (pending) {
            root.setTimeout(() => open('pending-bastion-event-modal'), 0);
        }
    });
    root.addEventListener('scroll', syncSticky, {passive: true});
    root.addEventListener('resize', syncSticky);

    const start = () => {
        app.bastionRecentAdapter?.consumeRestore?.();
        app.persistedFragment?.start({
            documentRef,
            storage: root.localStorage,
            onStored: bastion => app.bastionRecentAdapter?.saveCurrent?.(bastion),
        });
        syncClientState();
        syncTurnButtons();
        syncFacilityLevelOptions();
        syncBasicFacilitySpace();
        syncStableStartingMounts();
        syncSticky();
        renderHistory();
    };
    if (documentRef.readyState === 'loading') documentRef.addEventListener('DOMContentLoaded', start, {once: true});
    else start();
})(window);
